import { createContext } from 'react';
import { User } from '../../interfaces';

export interface AppState {
  user: {
    data: User | null;
    isLoading: boolean;
    selectedBusiness: string;
  };
  utm: {
    id: string;
    sourceCampaignId: string | null;
    employeeId: string | null;
    businessId: string | null;
    appointmentId: string | null;
    userId: string | null;
  } | null;
  confirmationModal: {
    status: 'idle' | 'awaiting' | 'resolved' | 'rejected';
    resolve: ((response: boolean) => void) | null;
    title: string;
    message: string;
    dismissible?: boolean;
    positiveText?: string;
    negativeText?: string;
    isSmall?: boolean;
    hideCloseButton?: boolean;
    noWrapButtonsText?: boolean;
  };
  loadingModal: {
    header: string;
    message: string;
    status: 'idle' | 'loading';
  };
}

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>; // Replace 'any' with the actual action type if you have one
}

const getExistingAttribution = () => {
  const obj = sessionStorage.getItem('utm_attribution');
  if (!obj || obj === 'null') return null;
  const parsed = JSON.parse(obj) as AppState['utm'];
  if (
    !parsed?.id ||
    !parsed?.sourceCampaignId ||
    (!parsed.employeeId && !parsed.businessId && !parsed.appointmentId)
  )
    return null;
  return parsed;
};

export const initialState: AppState = {
  user: {
    data: null,
    isLoading: true,
    selectedBusiness: sessionStorage.getItem('selectedBusiness') || '',
  },
  utm: getExistingAttribution(),
  confirmationModal: {
    status: 'idle',
    title: 'Confirm',
    message: 'Are you sure you want to continue?',
    dismissible: true,
    positiveText: 'Continue',
    negativeText: 'Cancel',
    resolve: null,
    isSmall: false,
    hideCloseButton: false,
  },
  loadingModal: {
    header: 'Loading...',
    message: 'This could take a minute, please do not refresh the page',
    status: 'idle',
  },
};

export type AppStateAction =
  | {
      type: 'SET_USER';
      payload: Partial<AppState['user']>;
    }
  | {
      type: 'SET_EXISTING_ATTRIBUTION';
      payload: AppState['utm'];
    }
  | {
      type: 'SET_CONFIRMATION_MODAL';
      payload: Partial<AppState['confirmationModal']>;
    }
  | {
      type: 'SET_LOADING_MODAL';
      payload: Partial<AppState['loadingModal']>;
    };

// Providing an initial value for dispatch as well (a no-op function in this case)
const defaultContextValue: AppContextType = {
  state: initialState,
  dispatch: () => {},
};

export const AppContext = createContext<AppContextType>(defaultContextValue);
